import React, { useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import {
  Bell, Moon, Globe, Shield,
  CheckCircle, AlertCircle, ChevronRight,
} from 'lucide-react';

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div className={`fixed bottom-6 right-6 z-[999] flex items-center gap-3 px-5 py-3 rounded-2xl shadow-modal text-white text-sm font-medium animate-slide-up`}
      style={{ background: ok ? 'linear-gradient(135deg,#059669,#047857)' : 'linear-gradient(135deg,#dc2626,#b91c1c)' }}>
      {ok ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {msg}
    </div>
  );
}

function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${value ? 'bg-electric-500' : 'bg-slate-200'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${value ? 'translate-x-5' : ''}`} />
    </button>
  );
}

const Settings: React.FC = () => {
  const { user, role, isSuperAdmin } = useAuth();
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showToast = (msg: string, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3000); };

  // Notification prefs (localStorage only for now)
  const [notifs, setNotifs] = useState({
    newTrip:      localStorage.getItem('notif_newTrip')      !== 'false',
    tripComplete:  localStorage.getItem('notif_tripComplete')  !== 'false',
    driverOffline: localStorage.getItem('notif_driverOffline') !== 'false',
    newOffer:      localStorage.getItem('notif_newOffer')      !== 'false',
  });

  const setNotif = (key: keyof typeof notifs, val: boolean) => {
    setNotifs((prev) => ({ ...prev, [key]: val }));
    localStorage.setItem(`notif_${key}`, String(val));
    showToast('Notification preference saved');
  };

  // Appearance (localStorage)
  const [darkMode, setDarkMode] = useState(localStorage.getItem('vilotid_dark') === '1');
  const toggleDark = () => {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('vilotid_dark', next ? '1' : '0');
    showToast(next ? 'Dark mode enabled (reload to apply)' : 'Light mode enabled (reload to apply)');
  };

  // Language
  const [lang, setLang] = useState(localStorage.getItem('vilotid_lang') ?? 'en');
  const changeLang = (l: string) => { setLang(l); localStorage.setItem('vilotid_lang', l); showToast('Language preference saved'); };

  const notifItems = [
    { key: 'newTrip'      as const, label: 'New trip created',        desc: 'When a driver starts a new trip' },
    { key: 'tripComplete' as const, label: 'Trip completed',           desc: 'When a driver completes a trip' },
    { key: 'driverOffline' as const, label: 'Driver goes offline',     desc: 'When an active driver disconnects' },
    { key: 'newOffer'     as const, label: 'New active offer',         desc: 'When a new ride offer comes in' },
  ];

  return (
    <Layout title="Settings" breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'Settings' }]}>
      <div className="max-w-2xl mx-auto space-y-5">

        {/* Account */}
        <div className="card p-6">
          <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <Shield className="w-4 h-4 text-electric-500" /> Account
          </h3>
          <p className="text-xs text-slate-400 mb-5">Manage your account details and security</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 border border-slate-100">
              <div>
                <div className="text-sm font-medium text-slate-700">Email</div>
                <div className="text-xs text-slate-400 mt-0.5">{user?.email}</div>
              </div>
              <span className="text-xs text-slate-400 bg-white border border-slate-200 px-2 py-1 rounded-lg">Managed by Supabase Auth</span>
            </div>
            <div className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 border border-slate-100">
              <div>
                <div className="text-sm font-medium text-slate-700">Role</div>
                <div className="text-xs text-slate-400 mt-0.5 capitalize">{role ?? 'operator'}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </div>
            {isSuperAdmin && (
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-purple-50 border border-purple-100">
                <div>
                  <div className="text-sm font-medium text-purple-700">Platform Admin Access</div>
                  <div className="text-xs text-purple-400 mt-0.5">You have full government-level access to all data</div>
                </div>
                <Shield className="w-4 h-4 text-purple-400" />
              </div>
            )}
          </div>
        </div>

        {/* Notifications */}
        <div className="card p-6">
          <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <Bell className="w-4 h-4 text-electric-500" /> Notifications
          </h3>
          <p className="text-xs text-slate-400 mb-5">Choose which events trigger in-dashboard alerts</p>
          <div className="space-y-3">
            {notifItems.map((item) => (
              <div key={item.key} className="flex items-center justify-between p-3.5 rounded-xl hover:bg-slate-50 border border-slate-100 transition">
                <div>
                  <div className="text-sm font-medium text-slate-700">{item.label}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{item.desc}</div>
                </div>
                <Toggle value={notifs[item.key]} onChange={(v) => setNotif(item.key, v)} />
              </div>
            ))}
          </div>
        </div>

        {/* Appearance */}
        <div className="card p-6">
          <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <Moon className="w-4 h-4 text-electric-500" /> Appearance
          </h3>
          <p className="text-xs text-slate-400 mb-5">Customize the dashboard look and feel</p>
          <div className="flex items-center justify-between p-3.5 rounded-xl border border-slate-100 hover:bg-slate-50 transition">
            <div>
              <div className="text-sm font-medium text-slate-700">Dark Mode</div>
              <div className="text-xs text-slate-400 mt-0.5">Switch between light and dark theme</div>
            </div>
            <Toggle value={darkMode} onChange={toggleDark} />
          </div>
        </div>

        {/* Language */}
        <div className="card p-6">
          <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <Globe className="w-4 h-4 text-electric-500" /> Language & Region
          </h3>
          <p className="text-xs text-slate-400 mb-5">Set your preferred display language</p>
          <select
            value={lang}
            onChange={(e) => changeLang(e.target.value)}
            className="input-premium max-w-xs"
          >
            <option value="en">English</option>
            <option value="sv">Svenska (Swedish)</option>
            <option value="ar">العربية (Arabic)</option>
            <option value="fr">Français (French)</option>
          </select>
        </div>

        {/* Danger zone */}
        <div className="card p-6 border border-red-100">
          <h3 className="font-semibold text-red-700 mb-1">Danger Zone</h3>
          <p className="text-xs text-slate-400 mb-5">Irreversible actions — proceed with caution</p>
          <button
            onClick={() => showToast('Contact your platform admin to delete your account', false)}
            className="px-5 py-2 rounded-xl border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50 transition"
          >
            Request Account Deletion
          </button>
        </div>

      </div>
      {toast && <Toast msg={toast.msg} ok={toast.ok} />}
    </Layout>
  );
};

export default Settings;
